import { PermanentBaseViewModel, ProductIntegrationVM, productApis } from "@payping/api";
import { FiMap, InfoCircleOutlined, MailOutlined, Phone, User } from "@payping/icons";
import { isWebUrl } from "@payping/utilities-shared/validations";
import {
  Button,
  Checkbox,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  TextArea,
  Tooltip,
  Typography,
} from "@payping/web-components";
import React, { ReactElement, useEffect, useState } from "react";
import { useAsync } from "react-use";
import { useAppTranslation } from "@/utils/useAppTranslation";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { LinkFormProps, LinkFormType } from "./types";
import { SwitchRow } from "@/core/component";

const LinkForm: React.FC<LinkFormProps> = (props: LinkFormProps) => {
  const {
    onDirectPaymentActiveChange,
    setMailerLiteListId,
    kavehNegarSmsText,
    setKavehNegarSmsText,
    formRef,
    loading,
    details,
  } = props;
  const { t } = useAppTranslation();
  const [kavehNegarModal, setKavehNegarModal] = useState<boolean>(false);
  const [mailerLiteModal, setMailerLiteModal] = useState<boolean>(false);

  const tmpIntegrationStatus: ProductIntegrationVM = {
    kavenegar: true,
    mailerLite: true,
  };

  const { value: integrationStatus = tmpIntegrationStatus } = useAsync(
    async () => productApis.productGetIntegrationStatus(),
    [],
  );

  useEffect(() => {
    formRef.setFieldsValue({
      ...details,
      customDesOption: Boolean(details?.customDesOption),
      customDesOptionMandatory: details?.customDesOption || PermanentBaseViewModel.CustomDesOptionEnum.Required,
      hasRedirect: Boolean(details?.redirectPage),
      emailOption: details?.emailOption === PermanentBaseViewModel.EmailOptionEnum.Required,
      nameOption: details?.nameOption === PermanentBaseViewModel.NameOptionEnum.Required,
      phoneOption: details?.phoneOption === PermanentBaseViewModel.PhoneOptionEnum.Required,
      redirectPage: details?.redirectPage || "",
      address: details?.getAddress ?? false,
    });
  }, [details, formRef]);

  return (
    <Flex vertical className={"tw-mt-4"}>
      {/* < سویچ فعالسازی لینک مستقیم پرداخت > */}
      <Form.Item name={"isActive"} valuePropName={"checked"}>
        <SwitchRow
          onChange={(checked) => {
            if (!checked) {
              formRef.setFieldsValue({ hasRedirect: false, customDesOption: false });
              formRef.resetFields([
                "nameOption",
                "emailOption",
                "phoneOption",
                "address",
                "redirectPage",
                "customDescriptionText",
              ]);
            }

            if (onDirectPaymentActiveChange) {
              onDirectPaymentActiveChange();
            }
          }}
          label={<Typography.Text className={"font-bold"}>
            {t("product.form.perma.label")}
            </Typography.Text>}
          loading={loading}
        />
      </Form.Item>
      {/* < سویچ  فعالسازی لینک مستقیم پرداخت /> */}

      <Form.Item
        noStyle
        shouldUpdate={(prev, curr): boolean =>
          prev.isActive !== curr.isActive || prev.customDesOption !== curr.customDesOption
        }
      >
        {(linkForm): ReactElement => {
          const isActive = linkForm.getFieldValue("isActive");
          const customDescription = linkForm.getFieldValue("customDesOption");

          /* اجباری - غیر اجباری */
          const selectAfter = (
            <Form.Item noStyle name={"customDesOptionMandatory"} initialValue={2}>
              <Select disabled={!customDescription}>
                <Select.Option value={PermanentBaseViewModel.CustomDesOptionEnum.Optional}>
                  {t("optional")}
                </Select.Option>
                <Select.Option value={PermanentBaseViewModel.CustomDesOptionEnum.Required}>
                  {t("mandatory")}
                </Select.Option>
              </Select>
            </Form.Item>
          );

          return (
            <Flex vertical gap={"middle"}>
              {/* < دریافت مشخصات اجباری > */}
              <Flex vertical gap={"small"} className={"tw-mb-6"}>
                <Typography.Text className={isActive ? "" : "color--gray500"}>
                  {t("product.form.perma.customQuestion")}
                </Typography.Text>
                <Space className={"tw-w-full"} style={{ flexWrap: "wrap", rowGap: "8px" }}>
                  <Form.Item className={"tw-m-0"} valuePropName={"checked"} name={"nameOption"}>
                    <Tag.CheckableTag
                      className={classNames("tw-m-0 tw-px-6", isActive ? "" : "color--gray500 tw-pointer-events-none")}
                      checked
                    >
                      <Space>
                        <User width={"12px"} />
                        {t("product.form.perma.name")}
                      </Space>
                    </Tag.CheckableTag>
                  </Form.Item>

                  <Form.Item className={"tw-m-0"} valuePropName={"checked"} name={"emailOption"}>
                    <Tag.CheckableTag
                      className={classNames("tw-m-0 tw-px-6", isActive ? "" : "color--gray500 tw-pointer-events-none")}
                      checked
                    >
                      <Space>
                        <MailOutlined width={12} />
                        {t("product.form.perma.email")}
                      </Space>
                    </Tag.CheckableTag>
                  </Form.Item>

                  <Form.Item className={"tw-m-0"} valuePropName={"checked"} name={"phoneOption"}>
                    <Tag.CheckableTag
                      className={classNames("tw-m-0 tw-px-6", isActive ? "" : "color--gray500 tw-pointer-events-none")}
                      checked
                    >
                      <Space>
                        <Phone width={12} />
                        {t("product.form.perma.phone")}
                      </Space>
                    </Tag.CheckableTag>
                  </Form.Item>

                  <Form.Item className={"tw-m-0"} valuePropName={"checked"} name={"address"}>
                    <Tag.CheckableTag
                      className={classNames("tw-m-0 tw-px-6", isActive ? "" : "color--gray500 tw-pointer-events-none")}
                      checked
                    >
                      <Space>
                        <FiMap width={12} />
                        {t("product.form.perma.address")}
                      </Space>
                    </Tag.CheckableTag>
                  </Form.Item>
                </Space>
              </Flex>
              {/* < دریافت مشخصات اجباری /> */}

              {/* < کاربر پس از پرداخت لینک ارجاع && سوال سفارشی > */}
              <Space size={"small"} direction={"vertical"} className={"w-100 mb-2"}>
                {/* < لینک ارجاع > */}
                <Form.Item shouldUpdate={(prev, curr): boolean => prev.hasRedirect !== curr.hasRedirect}>
                  {(form): ReactElement => {
                    const showSelect = form.getFieldValue("hasRedirect");
                    return (
                      <Space size={"large"} direction={"vertical"} className={"w-100"}>
                        {/* < سویچ > */}
                        <Form.Item noStyle name={"hasRedirect"} valuePropName={"checked"}>
                          <SwitchRow
                            loading={loading}
                            disabled={!isActive}
                            label={
                              <Typography.Text className={isActive ? "" : "color--gray500"}>
                                {t("product.form.perma.hasRedirect")}
                              </Typography.Text>
                            }
                            onChange={(checked) => {
                              // Update `hasRedirect` state
                              form.setFieldsValue({ hasRedirect: checked });

                              // Clear `redirectPage` if the switch is turned off
                              if (!checked) {
                                form.setFieldValue("redirectPage", null);
                                form.resetFields(["redirectPage"]);
                              }
                            }}
                          />
                        </Form.Item>
                        {/* < سویچ /> */}

                        {/* < اینپوت لینک > */}
                        <Form.Url<LinkFormType>
                          form={formRef}
                          disabled={!showSelect}
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, url) {
                                const fullUrl = getFieldValue("redirectPage");
                                const disabled = !form.getFieldValue("hasRedirect");

                                // check 'hasRedirect' switch
                                if (disabled) return Promise.resolve();

                                // checks url is not empty
                                if (!url) return Promise.reject(t("product.form.perma.requiredField"));

                                return !isWebUrl(fullUrl, { allowWithoutProtocol: true })
                                  ? Promise.reject(t("error.url"))
                                  : Promise.resolve();
                              },
                            }),
                          ]}
                          name={"redirectPage"}
                          label={
                            <Typography.Text className={classNames(isActive ? "" : "color--gray500")}>
                              {t("product.form.perma.redirectPage")}
                            </Typography.Text>
                          }
                          placeholder={"www.payping.ir"}
                        />
                        {/* < اینپوت لینک /> */}
                      </Space>
                    );
                  }}
                </Form.Item>
                {/* < لینک ارجاع /> */}

                {/* < سوال سفارشی > */}
                <Form.Item shouldUpdate={(prev, curr): boolean => prev.customDesOption !== curr.customDesOption}>
                  {(form) => (
                    <Space size={"large"} direction={"vertical"} className={"tw-w-full"}>
                      <Form.Item noStyle name={"customDesOption"} valuePropName={"checked"}>
                        <SwitchRow
                          disabled={!isActive}
                          label={
                            <Typography.Text className={isActive ? "" : "color--gray500"}>
                              {t("product.form.perma.customDesQuestion")}
                            </Typography.Text>
                          }
                          loading={loading}
                          onChange={(checked) => {
                            // Update `customDesOption` state
                            form.setFieldsValue({ customDesOption: checked });

                            // Clear `redirectPage` if the switch is turned off
                            if (!checked) {
                              form.resetFields(["customDescriptionText"]);
                            }
                          }}
                        />
                      </Form.Item>

                      <Form.Item
                        label={
                          <Typography.Text className={isActive ? "" : "color--gray500"}>
                            {t("product.form.perma.questionText")}
                          </Typography.Text>
                        }
                        name={"customDescriptionText"}
                        rules={[
                          {
                            validator(_, text) {
                              const disabled = !form.getFieldValue("customDesOption");

                              // checks 'customDesOption' switch
                              if (disabled) return Promise.resolve();

                              // checks text is not empty
                              if (!text) return Promise.reject(t("product.form.perma.requiredField"));
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Input
                          disabled={!customDescription}
                          placeholder={t("product.form.perma.customQuestionPlaceHolder")}
                          addonAfter={selectAfter}
                        />
                      </Form.Item>
                    </Space>
                  )}
                </Form.Item>
                {/* < سوال سفارشی /> */}
              </Space>
              {/* < کاربر پس از پرداخت لینک ارجاع && سوال سفارشی /> */}

              {/* < اطلاع رسانی پس از پرداخت > */}
              <Space direction={"vertical"} size={"large"} className={"tw-mb-8 tw-w-full"}>
                <Typography.Text className={isActive ? "" : "color--gray500"}>
                  {t("product.form.perma.notices")}:
                </Typography.Text>

                {/* < کاوه نگار > */}
                <div className={"tw-flex tw-justify-between"}>
                  {/* < چک باکس > */}
                  <Space className={"tw-flex tw-items-center"}>
                    <Checkbox
                      disabled={!isActive || !integrationStatus.kavenegar}
                      checked={integrationStatus?.kavenegar}
                    >
                      {t("services.list.kavehNegar.name")}
                    </Checkbox>
                  </Space>
                  {/* < چک باکس /> */}

                  <Space>
                    {integrationStatus?.kavenegar ? (
                      <Button type={"link"} disabled={!isActive} onClick={() => setKavehNegarModal(true)}>
                        {t("product.form.perma.kavehNegar.prefix")}
                      </Button>
                    ) : (
                      <Tooltip
                        title={
                          isActive && (
                            <Typography.Text className={"color--white"}>
                              جهت فعال‌سازی اطلاع رسانی از طریق پیامک، نسبت به اتصال
                              <Link to={"/connections/plugins/details/kavehNegar"} style={{ color: "lightblue" }}>
                                {" سرویس کاوه نگار "}
                              </Link>
                              اقدام نمایید.
                            </Typography.Text>
                          )
                        }
                      >
                        <Button
                          type={"text"}
                          icon={<InfoCircleOutlined width={14} height={14} />}
                          disabled={!isActive || !integrationStatus.kavenegar}
                        >
                          {t("inactive")}
                        </Button>
                      </Tooltip>
                    )}
                  </Space>

                  <Modal
                    title={t("product.form.perma.kavehNegar.prefix")}
                    open={kavehNegarModal}
                    onCancel={() => {
                      setKavehNegarModal(false);
                      setKavehNegarSmsText(undefined);
                    }}
                    onOk={() => setKavehNegarModal(false)}
                  >
                    <Form.Item name={"smsText"}>
                      <TextArea
                        onChange={(textArea: React.ChangeEvent<HTMLTextAreaElement>) => {
                          setKavehNegarSmsText(textArea.target.value);
                        }}
                        disabled={!integrationStatus?.kavenegar}
                        value={kavehNegarSmsText}
                      />
                    </Form.Item>
                  </Modal>
                </div>
                {/* < کاوه نگار /> */}

                {/* < میلر لایت > */}
                <div className={"tw-flex tw-justify-between"}>
                  <Space className={"tw-flex tw-items-center"}>
                    <Checkbox
                      disabled={!isActive || !integrationStatus?.mailerLite}
                      checked={integrationStatus?.mailerLite}
                    >
                      {t("services.list.mailerLite.name")}
                    </Checkbox>
                  </Space>

                  <Space>
                    {integrationStatus?.mailerLite ? (
                      <Button type={"link"} disabled={!isActive} onClick={() => setMailerLiteModal(true)}>
                        {t("product.form.perma.mailerLite.prefix")}
                      </Button>
                    ) : (
                      <Tooltip
                        title={
                          isActive && (
                            <Typography.Text className={"color--white"}>
                              جهت فعال‌سازی اطلاع رسانی از طریق ایمیل، نسبت به اتصال سرویس
                              <Link to={"/connections/plugins/details/mailerLite"} style={{ color: "lightblue" }}>
                                {" میلرلایت "}
                              </Link>
                              اقدام نمایید.
                            </Typography.Text>
                          )
                        }
                      >
                        <Button
                          type={"text"}
                          icon={<InfoCircleOutlined width={14} height={14} />}
                          disabled={!isActive || !integrationStatus?.mailerLite}
                        >
                          {t("inactive")}
                        </Button>
                      </Tooltip>
                    )}
                  </Space>

                  <Modal
                    title={t("product.form.perma.mailerLite.prefix")}
                    open={mailerLiteModal}
                    onOk={() => setMailerLiteModal(false)}
                    onCancel={() => {
                      setMailerLiteModal(false);
                      setMailerLiteListId(undefined);
                    }}
                  >
                    <Form.Item tooltip={t("product.form.perma.mailerLite.help")} name={"mailerLiteListId"}>
                      <Select
                        onChange={(select: React.ChangeEvent<HTMLSelectElement>) => {
                          setMailerLiteListId(select.target.value);
                        }}
                        disabled={!integrationStatus?.mailerLite}
                        options={integrationStatus?.mailerLiteList?.map((mailerLiteItem) => ({
                          label: mailerLiteItem.item1,
                          title: mailerLiteItem.item1,
                          value: mailerLiteItem.item2,
                        }))}
                      />
                    </Form.Item>
                  </Modal>
                </div>
                {/* < میلر لایت /> */}
              </Space>
              {/* < اطلاع رسانی پس از پرداخت /> */}
            </Flex>
          );
        }}
      </Form.Item>
    </Flex>
  );
};

export default LinkForm;
